/**
 * Privacy banner component
 * GDPR-compliant; allows users to control data collection
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfirm } from './ui/ConfirmDialog';
import { createStore } from '@resource-kit/controllers';

const CONSENT_KEY = 'hgs-analytics-consent';
export const CONSENT_STORAGE_KEY = `device:${CONSENT_KEY}`;

interface ConsentState {
  analytics: boolean;
  timestamp: number;
}

const consentStore = createStore<ConsentState>({
  key: CONSENT_KEY,
  version: 1,
  scope: 'device',
  sanitize: (raw) => {
    if (typeof raw !== 'object' || raw === null) return null;
    const consent = raw as Record<string, unknown>;
    return typeof consent.analytics === 'boolean' &&
      typeof consent.timestamp === 'number' &&
      Number.isFinite(consent.timestamp)
      ? { analytics: consent.analytics, timestamp: consent.timestamp }
      : null;
  },
});

export const PrivacyBanner: React.FC = () => {
  const { t } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);
  const { confirm, alert } = useConfirm();

  useEffect(() => {
    // 检查是否已有同意记录
    const savedConsent = consentStore.read();
    if (savedConsent) {
      // 检查同意记录是否过期（6个月）
      const sixMonthsAgo = Date.now() - 6 * 30 * 24 * 60 * 60 * 1000;
      if (savedConsent.timestamp > sixMonthsAgo) {
        return; // 已有有效同意记录，不显示横幅
      }
    }

    // 显示横幅
    setShowBanner(true);
  }, []);

  const handleConsent = (analyticsConsent: boolean) => {
    const newConsent: ConsentState = {
      analytics: analyticsConsent,
      timestamp: Date.now(),
    };

    setShowBanner(false);
    consentStore.write(newConsent);

    // 触发存储事件，让 GoogleAnalytics 组件响应
    window.dispatchEvent(
      new StorageEvent('storage', {
        key: CONSENT_STORAGE_KEY,
        newValue: JSON.stringify(newConsent),
      }),
    );

    // 如果 GA 已加载，更新同意状态
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: analyticsConsent ? 'granted' : 'denied',
      });
    }
  };

  const handleSettings = async () => {
    // 可以扩展为更详细的设置对话框
    const userChoice = await confirm({
      title: t('privacy.settingsTitle'),
      message: t('privacy.settingsMessage'),
      type: 'info',
      confirmText: t('privacy.allowCollection'),
      cancelText: t('privacy.decline'),
    });
    handleConsent(userChoice);
  };

  if (!showBanner) {
    return null;
  }

  return (
    // From `md` the sidebar is persistent (#2511), so the banner starts to its right
    // and leaves the sidebar's user block reachable. `AppLayout` sets
    // `--app-sidebar-width` on <body> (232px, or the icon rail when collapsed)
    // only while it renders the sidebar; public
    // routes (e.g. `/login`) leave it unset and the banner spans the viewport.
    // On a phone, `MobileTabBar` sets `--app-tabbar-height` and the banner sits
    // above the bottom tab bar instead of over it (#2487).
    <div className="fixed bottom-[var(--app-tabbar-height,0px)] left-0 right-0 md:left-[var(--app-sidebar-width,0px)] bg-card border-t border-border text-foreground p-4 shadow-lg z-50">
      <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex-1 text-sm">
          <p className="mb-2">
            🍪 <strong>{t('privacy.title')}</strong>
          </p>
          <p className="text-muted-foreground">
            {t('privacy.message')}
            <a
              href="#"
              className="text-accent hover:text-accent-hover underline ml-1"
              onClick={async (e) => {
                e.preventDefault();
                await alert(t('privacy.policyTitle'), t('privacy.policyDetails'), 'info');
              }}
            >
              {t('privacy.learnMore')}
            </a>
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => handleConsent(false)}
            className="px-4 py-2 text-sm bg-secondary hover:bg-secondary/80 rounded transition-colors"
          >
            {t('privacy.essential')}
          </button>
          <button
            onClick={handleSettings}
            className="px-4 py-2 text-sm bg-secondary hover:bg-secondary/80 rounded transition-colors"
          >
            {t('privacy.customize')}
          </button>
          <button
            onClick={() => handleConsent(true)}
            className="px-4 py-2 text-sm bg-primary text-primary-foreground hover:bg-primary/90 rounded transition-colors"
          >
            {t('privacy.acceptAll')}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * 检查用户是否同意分析
 */
export function hasAnalyticsConsent(): boolean {
  const savedConsent = consentStore.read();
  if (!savedConsent) return false;

  const sixMonthsAgo = Date.now() - 6 * 30 * 24 * 60 * 60 * 1000;
  return savedConsent.timestamp > sixMonthsAgo && savedConsent.analytics;
}

/**
 * 撤销所有同意
 */
export function revokeConsent(): void {
  consentStore.remove();

  if (window.gtag) {
    window.gtag('consent', 'update', {
      analytics_storage: 'denied',
    });
  }

  // 刷新页面以应用更改
  window.location.reload();
}
